import { size, uniqBy, values } from "lodash-es";
import { useMemo } from "react";

import { useBandContext, useEventContext } from "Bands/contexts";
import { eventBlockSelectorCreator } from "Bands/state";
import {
  EventUniqueBlocks,
  IBandUser,
  IEventPeopleBlock,
  InvitationStatus,
  PeopleBlockStatus,
} from "Bands/types";
import { getMemberName, getPeopleBlockStatus } from "Bands/utils";
import { useSelector } from "react-redux";
import { UserMultiple } from "shared/assets/icons";
import {
  ISelectCategoryOption,
  ISelectOption,
} from "shared/components/Form/Fields/select/types";
import { FlexContainer, Icon } from "shared/styles";
import { getStatus } from "shared/styles/defaults";
import { IPeoplePickerSettings } from "shared/types";

export interface IBandUserSelectOption extends ISelectOption {
  user?: string;
}

export const useLogic = (
  settings?: IPeoplePickerSettings
): [IBandUserSelectOption[], ISelectCategoryOption[], boolean] => {
  const { eventBlocks } = useEventContext();
  const { allBandUsers, userBands, getCategory } = useBandContext();

  const peopleBlockId =
    eventBlocks?.find((block) => block.type === EventUniqueBlocks.people)?.id ||
    "";

  const peopleBlockSelector = eventBlockSelectorCreator(
    peopleBlockId,
    EventUniqueBlocks.people
  );

  const peopleBlockske = useSelector(peopleBlockSelector) as IEventPeopleBlock;
  const peopleBlockUsers = peopleBlockske?.users;

  const getInvitationStatus = (status?: InvitationStatus) => {
    switch (status) {
      case InvitationStatus.invitation_sent:
        return "(pending member)";
      case InvitationStatus.invitation_not_sent:
        return "(not yet invited member)";
      default:
        return "";
    }
  };

  const hasInvitedUsers = useMemo(() => {
    return allBandUsers.some(({ invitation }) => invitation);
  }, [allBandUsers]);

  const filteredBandUsers = useMemo(() => {
    let data: IBandUser[] = allBandUsers;

    // Include users from all bands if option is true
    if (settings?.includeUsersFromAllBands) {
      data = [];
      userBands.forEach((userBand) => {
        if (userBand.users) {
          data = [...data, ...userBand.users];
        }
      });

      // Invited users are always filtered out if includeUsersFromAllBands is enabled, so check on both.
      data = uniqBy(data, (band) => band.user?.id || band.invitation);
    }

    // Filter invited users
    if (!settings?.includeInvitedUsers) {
      data = data.filter(({ invitation }) => !invitation);
    }

    // Filter excluded options from id

    if (settings?.excludeOptions && size(settings.excludeOptions) > 0) {
      data = data.filter(({ id, user }) => {
        return !(
          settings?.excludeOptions?.includes(id) ||
          (user && settings?.excludeOptions?.includes(user.id))
        );
      });
    }

    data = uniqBy(data, (bandUser) => bandUser.email);

    return data;
  }, [
    userBands,
    allBandUsers,
    settings?.excludeOptions,
    settings?.includeInvitedUsers,
    settings?.includeUsersFromAllBands,
  ]);

  const options = useMemo(
    (): IBandUserSelectOption[] =>
      filteredBandUsers
        .map((bUser) => {
          const { id, nickname, firstname, lastname, invitation, user } = bUser;
          const searchableName = `${nickname} ${firstname} ${lastname}`;

          return {
            label: getMemberName(bUser, settings?.hideNickname),
            suffix: getInvitationStatus(invitation?.status),
            searchableLabel: `${searchableName}`,
            value: id,
            user: user && user?.id,
          };
        })
        .sort(
          (a: IBandUserSelectOption, b: IBandUserSelectOption) =>
            +(a.label.toLocaleLowerCase() > b.label.toLocaleLowerCase()) ||
            -(a.label.toLocaleLowerCase() < b.label.toLocaleLowerCase())
        ),
    [filteredBandUsers, settings?.hideNickname]
  );

  const categories = useMemo((): ISelectCategoryOption[] => {
    if (settings?.hideCategories) return [];

    // Create categories based on band users categories
    const memberCategories = values(
      filteredBandUsers?.reduce((list, user) => {
        if (user.category) {
          const { id } = user.category;
          const label = getCategory(id)?.label;

          if (!list[id]) {
            list[id] = { label, value: id, items: [], type: "category" };
          }

          list[id].items.push({ value: user.id, label: getMemberName(user) });
        }

        return list;
      }, {} as Record<string, ISelectCategoryOption>)
    ).map((category) => {
      const itemCount = category.items.length;
      const categoryObject = getCategory(category.value);

      return {
        ...category,
        label: `${categoryObject?.label} (${itemCount} ${
          itemCount === 1 ? "member" : "members"
        })`,
        labelComponent: (
          <FlexContainer gap="xxs" lineHeight="l">
            <Icon as={UserMultiple} />
            {categoryObject?.label}
          </FlexContainer>
        ),
      };
    });

    if (settings?.filterOnStatus) {
      // Create categories based on people block users statuses
      const peopleBlockCategories = values(
        peopleBlockUsers
          ?.filter((user) =>
            settings?.includeInvitedUsers ? true : !user.user?.invitation
          )
          .reduce((list, user) => {
            if (
              user.status !== PeopleBlockStatus.none &&
              user.status !== PeopleBlockStatus.not_attending
            ) {
              if (!list[user.status]) {
                list[user.status] = {
                  label: user.status,
                  value: user.status,
                  items: [],
                  type: "category",
                };
              }

              list[user.status].items.push({
                value: user.user?.id,
                label: getMemberName(user.user),
              });
            }

            return list;
          }, {} as Record<string, ISelectCategoryOption>)
      )
        .map((category) => {
          const itemCount = category.items.length;

          const statusObject = getPeopleBlockStatus(
            category.value as PeopleBlockStatus
          );

          const optionLabel = `${statusObject?.categoryLabel} (${itemCount} ${
            itemCount === 1 ? "member" : "members"
          })`;

          return {
            ...category,
            label: optionLabel,
            labelComponent: (
              <FlexContainer gap="xxs" lineHeight="l">
                <Icon
                  as={statusObject?.icon}
                  color={getStatus(statusObject?.iconStatus).iconColor}
                />
                {optionLabel}
              </FlexContainer>
            ),
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));

      return [...peopleBlockCategories, ...memberCategories];
    }

    return memberCategories;
  }, [
    filteredBandUsers,
    settings?.hideCategories,
    getCategory,
    peopleBlockUsers,
    settings?.filterOnStatus,
    settings?.includeInvitedUsers,
  ]);

  return [options, categories, hasInvitedUsers];
};
